import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getReflexAdminErrorMessage,
  ReflexListParams,
  reflexesService,
  SaveReflexInput,
} from "../../services/admin/reflexes.service";

const REFLEXES_QUERY_KEY = ["admin", "reflexes"] as const;

export function useReflexes(params: ReflexListParams) {
  return useQuery({
    queryKey: [...REFLEXES_QUERY_KEY, params],
    queryFn: () => reflexesService.list(params),
    placeholderData: (previous) => previous,
  });
}

export function useReflexAdminOptions() {
  return useQuery({
    queryKey: [...REFLEXES_QUERY_KEY, "options"],
    queryFn: reflexesService.getOptions,
  });
}

export function useCreateReflex() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: SaveReflexInput) => reflexesService.create(input),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: REFLEXES_QUERY_KEY });
      notifications.show({
        title: "Reflex created",
        message: "The reflex is available for accessioning.",
        color: "green",
      });
    },
  });
}

export function useCreateReflexVersion() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: SaveReflexInput }) =>
      reflexesService.createVersion(id, input),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: REFLEXES_QUERY_KEY });
      notifications.show({
        title: "Reflex version created",
        message: "Future evaluations use the updated rules.",
        color: "green",
      });
    },
  });
}

export function useUpdateReflexStatus() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      reflexesService.updateStatus(id, isActive),
    onSuccess: (_, variables) => {
      client.invalidateQueries({ queryKey: REFLEXES_QUERY_KEY });
      notifications.show({
        title: variables.isActive ? "Reflex activated" : "Reflex deactivated",
        message: variables.isActive
          ? "The reflex is available for new orders."
          : "The reflex is hidden from new orders.",
        color: variables.isActive ? "green" : "orange",
      });
    },
    onError: (error) =>
      notifications.show({
        title: "Reflex status was not changed",
        message: getReflexAdminErrorMessage(error),
        color: "red",
      }),
  });
}
